/**
 * The demo installation's badge in the top bar (ADR-039, REQUESTS #153 a): "Demo", and what it means. A toggletip:
 * a click or tap opens (and closes) the sentence, a mouse pointer or keyboard focus shows it while there; Escape or
 * leaving the badge closes it. The sentence is always in the tree (visually hidden while closed), so it is the
 * badge's description for screen readers and nothing is mounted again when it opens. No Radix: it stays tiny in the
 * first load.
 */
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Chip } from "@brevalt/ui";

const HINT_ID = "demo-badge-hint";

export function DemoBadge() {
  const { t } = useTranslation();
  const [pinned, setPinned] = useState(false);
  const [shown, setShown] = useState(false);
  // A focus that comes from a press (mouse or touch) doesn't open: the click decides.
  const pressed = useRef(false);
  const open = pinned || shown;
  const close = () => {
    setPinned(false);
    setShown(false);
  };
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        className="rounded-chip"
        aria-describedby={HINT_ID}
        aria-expanded={open}
        aria-controls={HINT_ID}
        data-testid="demo-badge"
        onPointerDown={() => {
          pressed.current = true;
        }}
        onPointerEnter={(e) => {
          if (e.pointerType === "mouse") setShown(true);
        }}
        onPointerLeave={(e) => {
          if (e.pointerType === "mouse") setShown(false);
        }}
        onFocus={() => {
          if (!pressed.current) setShown(true);
          pressed.current = false;
        }}
        onBlur={close}
        onKeyDown={(e) => {
          if (e.key === "Escape") close();
        }}
        onClick={() => {
          pressed.current = false;
          if (pinned) close();
          else setPinned(true);
        }}
      >
        <Chip tone="brand" icon={null}>
          {t("shell.demo_badge")}
        </Chip>
      </button>
      <span
        id={HINT_ID}
        role="tooltip"
        data-open={open}
        className={
          open
            ? "absolute right-0 top-full z-(--z-dropdown) mt-2 w-72 rounded-card border border-border bg-surface p-3 text-xs leading-relaxed text-text shadow-overlay"
            : "sr-only"
        }
      >
        {t("shell.demo_badge_hint")}
      </span>
    </span>
  );
}
